import { useState, useContext, useMemo, useEffect, useRef } from 'react';
import { Calendar as CalendarIcon, X, Sparkles, MousePointerClick, ChevronLeft, ChevronRight } from 'lucide-react';
import { FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isToday,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { PreviewCardShell } from './PreviewCardShell';
import { ChatContext } from '@/contexts/ChatContext';

/**
 * Normalizes platform value(s) to an array of valid platform types.
 * Handles: single string, array, comma-separated string, case variations, "YouTube Shorts"
 */
const normalizePlatforms = (platform: PlatformType | PlatformType[] | string): PlatformType[] => {
  let platforms: string[];

  if (Array.isArray(platform)) {
    platforms = platform;
  } else if (typeof platform === 'string') {
    // Split by comma if it's a comma-separated string
    platforms = platform.includes(',')
      ? platform.split(',').map(p => p.trim())
      : [platform];
  } else {
    return [];
  }

  // Normalize each platform string to lowercase and handle variations
  return platforms
    .map(p => {
      const normalized = p.toLowerCase().trim();
      // Handle "youtube shorts" -> "youtube"
      if (normalized.includes('youtube')) return 'youtube';
      if (normalized.includes('instagram')) return 'instagram';
      if (normalized.includes('tiktok') || normalized.includes('tik tok')) return 'tiktok';
      return normalized;
    })
    .filter((p): p is PlatformType =>
      p === 'instagram' || p === 'tiktok' || p === 'youtube'
    );
};

type PlatformType = 'instagram' | 'tiktok' | 'youtube';

interface CalendarEvent {
  date: string;
  platform: PlatformType | PlatformType[] | string; // Support single, array, or comma-separated string
  title: string;
  time?: string;
}

interface CalendarMockupProps {
  month: string;
  events: string | CalendarEvent[] | null | undefined;
  previewLabel?: string;
}

const YEAR_MONTH_RE = /^(\d{4})-(\d{2})$/;
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const CALENDAR_DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

const toLocalDate = (year: number, monthIndex: number, day: number): Date =>
  new Date(year, monthIndex, day);

export const parseCalendarMonth = (month: string): Date => {
  const trimmed = (month || '').trim();
  const yearMonthMatch = trimmed.match(YEAR_MONTH_RE);

  if (yearMonthMatch) {
    const year = Number(yearMonthMatch[1]);
    const monthIndex = Number(yearMonthMatch[2]) - 1;

    if (Number.isFinite(year) && Number.isFinite(monthIndex) && monthIndex >= 0 && monthIndex <= 11) {
      return toLocalDate(year, monthIndex, 1);
    }
  }

  const isoDateMatch = trimmed.match(ISO_DATE_RE);
  if (isoDateMatch) {
    const year = Number(isoDateMatch[1]);
    const monthIndex = Number(isoDateMatch[2]) - 1;
    const day = Number(isoDateMatch[3]);

    if (
      Number.isFinite(year) &&
      Number.isFinite(monthIndex) &&
      Number.isFinite(day) &&
      monthIndex >= 0 &&
      monthIndex <= 11 &&
      day >= 1 &&
      day <= 31
    ) {
      return toLocalDate(year, monthIndex, day);
    }
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  console.warn('Failed to parse month, using current month:', month);
  return new Date();
};

const normalizeCalendarEvent = (value: unknown): CalendarEvent | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const date = typeof candidate.date === 'string' ? candidate.date.trim() : '';
  const title = typeof candidate.title === 'string' ? candidate.title.trim() : '';
  const platform = candidate.platform;

  if (!title || !platform) {
    return null;
  }

  return {
    date,
    title,
    platform: platform as CalendarEvent['platform'],
    time: typeof candidate.time === 'string' ? candidate.time.trim() : undefined,
  };
};

export const parseCalendarEvents = (events: CalendarMockupProps['events']): CalendarEvent[] => {
  const normalizeArray = (items: unknown[]): CalendarEvent[] =>
    items
      .map(normalizeCalendarEvent)
      .filter((event): event is CalendarEvent => event !== null);

  if (Array.isArray(events)) {
    return normalizeArray(events);
  }

  if (typeof events !== 'string') {
    return [];
  }

  const trimmed = events.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return normalizeArray(parsed);
    }

    // Support double-encoded JSON strings, e.g. "\"[{...}]\""
    if (typeof parsed === 'string') {
      const nested = JSON.parse(parsed);
      return Array.isArray(nested) ? normalizeArray(nested) : [];
    }
  } catch (error) {
    console.warn('Failed to parse calendar events:', error);
  }

  return [];
};

const isValidCalendarDateKey = (value: string): boolean => {
  if (!CALENDAR_DATE_KEY_RE.test(value)) {
    return false;
  }

  const parsed = new Date(`${value}T00:00:00`);
  return !Number.isNaN(parsed.getTime()) && format(parsed, 'yyyy-MM-dd') === value;
};

const isDateInMonth = (dateKey: string, monthDate: Date): boolean => {
  if (!isValidCalendarDateKey(dateKey)) {
    return false;
  }

  const parsed = new Date(`${dateKey}T00:00:00`);
  return (
    parsed.getFullYear() === monthDate.getFullYear() &&
    parsed.getMonth() === monthDate.getMonth()
  );
};

export const mapCalendarEventsForMonth = (
  events: CalendarEvent[],
  monthDate: Date
): { eventList: CalendarEvent[]; usedDatelessFallback: boolean } => {
  const validMonthEvents = events.filter((event) => isDateInMonth(event.date, monthDate));

  // Use actual dates only if: no events, OR ALL events are in the selected month
  // Otherwise, use dateless fallback to ensure all events are shown
  if (events.length === 0 || validMonthEvents.length === events.length) {
    return { eventList: validMonthEvents, usedDatelessFallback: false };
  }

  const daysInMonth = endOfMonth(monthDate).getDate();
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const remapped = events.map((event, index) => {
    const day = Math.min(index + 1, daysInMonth);
    const dateKey = format(new Date(year, month, day), 'yyyy-MM-dd');

    return {
      ...event,
      date: dateKey,
    };
  });

  return {
    eventList: remapped,
    usedDatelessFallback: true,
  };
};

const PlatformBadge = ({
  platform,
  mini = false,
}: {
  platform: string;
  mini?: boolean;
}) => {
  const configs: Record<
    string,
    { Icon: any; color: string; label: string }
  > = {
    instagram: {
      Icon: FaInstagram,
      color: 'bg-[#E4405F]',
      label: 'IG',
    },
    tiktok: {
      Icon: FaTiktok,
      color: 'bg-[#00A8A3]',
      label: 'TT',
    },
    youtube: {
      Icon: FaYoutube,
      color: 'bg-[#FF0000]',
      label: 'YT',
    },
  };

  const config = configs[platform];
  if (!config) return null;

  if (mini) {
    return (
      <div
        className={`w-5 h-5 rounded-full ${config.color} flex items-center justify-center`}
      >
        <span className="text-white text-[8px] font-bold">{config.label}</span>
      </div>
    );
  }

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${config.color}`}
    >
      <config.Icon className="w-3 h-3 text-white" />
      <span className="text-white text-xs font-semibold">{config.label}</span>
    </div>
  );
};

const CalendarDay = ({
  date,
  events,
  isCurrentMonth,
  onClick,
  isClicked,
  hideDayNumber = false,
}: {
  date: Date;
  events: CalendarEvent[];
  isCurrentMonth: boolean;
  onClick?: () => void;
  isClicked?: boolean;
  hideDayNumber?: boolean;
}) => {
  const dayNumber = format(date, 'd');
  const eventCount = events?.length || 0;
  const hasEvents = eventCount > 0;

  return (
    <div
      className={`
      relative min-h-[80px] p-1.5 border border-zinc-800
      ${isCurrentMonth ? 'bg-zinc-900/30' : 'bg-zinc-900/10'}
      ${!hideDayNumber && isToday(date) ? 'ring-2 ring-blue-500 ring-inset' : ''}
      ${hasEvents ? 'cursor-pointer hover:bg-zinc-800/50 transition-colors' : ''}
      ${isClicked ? 'opacity-50 bg-zinc-700/20' : ''}
    `}
      onClick={hasEvents ? onClick : undefined}
    >
      {/* Day number */}
      {!hideDayNumber && (
        <div
          className={`text-xs font-semibold mb-1 ${isCurrentMonth ? 'text-zinc-50' : 'text-zinc-600'}`}
        >
          {dayNumber}
        </div>
      )}

      {/* Event display logic */}
      {eventCount === 0 && null}

      {eventCount === 1 && (() => {
        const platforms = normalizePlatforms(events[0].platform);
        return (
          <div className="bg-zinc-800/50 rounded px-1.5 py-1 backdrop-blur-sm">
            <div className="flex items-center gap-1 mb-0.5 flex-wrap">
              {platforms.map((p) => (
                <PlatformBadge key={p} platform={p} mini />
              ))}
            </div>
            <p className="text-[10px] text-zinc-200 line-clamp-2 leading-tight">
              {events[0].title}
            </p>
          </div>
        );
      })()}

      {eventCount === 2 && (
        <div className="space-y-1">
          {events.slice(0, 2).map((event, idx) => {
            const platforms = normalizePlatforms(event.platform);
            return (
              <div key={idx} className="flex items-center gap-1">
                <div className="flex gap-0.5">
                  {platforms.slice(0, 2).map((p) => (
                    <PlatformBadge key={p} platform={p} mini />
                  ))}
                </div>
                <p className="text-[9px] text-zinc-300 truncate flex-1">
                  {event.title}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {eventCount >= 3 && (() => {
        // Collect all unique platforms across all events
        const allPlatforms = events.flatMap(e => normalizePlatforms(e.platform));
        const uniquePlatforms = [...new Set(allPlatforms)];
        return (
          <div className="bg-zinc-800/70 rounded px-2 py-1.5 text-center backdrop-blur-sm">
            <div className="text-base font-bold text-zinc-50">{eventCount}</div>
            <div className="text-[9px] text-zinc-400">posts</div>
            <div className="flex items-center justify-center gap-0.5 mt-1">
              {uniquePlatforms.slice(0, 3).map((platform) => (
                <PlatformBadge key={platform} platform={platform} mini />
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export const CalendarMockup = ({
  month,
  events,
  previewLabel = 'Content Calendar',
}: CalendarMockupProps) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [clickedDates, setClickedDates] = useState<Set<string>>(new Set());
  const chatContext = useContext(ChatContext);
  const hasLoadedFromStorage = useRef(false);

  // Load clicked dates from localStorage
  useEffect(() => {
    const interacted = localStorage.getItem('calendar_interacted') === 'true';
    setHasInteracted(interacted);

    const storedClickedDates = localStorage.getItem('calendar_clicked_dates');
    if (storedClickedDates) {
      try {
        const parsed = JSON.parse(storedClickedDates);
        if (Array.isArray(parsed)) {
          setClickedDates(new Set(parsed));
        }
      } catch (error) {
        console.warn('Failed to parse clicked dates from localStorage:', error);
      }
    }
    hasLoadedFromStorage.current = true;
  }, []);

  // Persist clicked dates to localStorage whenever they change
  useEffect(() => {
    // Only persist after initial load to avoid overwriting on mount
    if (hasLoadedFromStorage.current) {
      try {
        localStorage.setItem('calendar_clicked_dates', JSON.stringify([...clickedDates]));
      } catch (error) {
        console.warn('Failed to save clicked dates to localStorage:', error);
      }
    }
  }, [clickedDates]);

  const markDateAsVisited = (dateKey: string) => {
    setClickedDates((prev) => {
      if (prev.has(dateKey)) return prev;
      const next = new Set(prev);
      next.add(dateKey);
      return next;
    });
  };

  const handleDayClick = (dateKey: string) => {
    setSelectedDate(dateKey);
    if (!hasInteracted) {
      setHasInteracted(true);
      localStorage.setItem('calendar_interacted', 'true');
    }
    markDateAsVisited(dateKey);
  };

  // Parse month (support both "2026-04" and "April 2026")
  const monthDate = useMemo(() => {
    return parseCalendarMonth(month);
  }, [month]);

  // Parse events and group by date
  const { eventList, eventsByDate, usedDatelessFallback } = useMemo(() => {
    const parsedEvents = parseCalendarEvents(events);
    const { eventList: mappedEventList, usedDatelessFallback } = mapCalendarEventsForMonth(parsedEvents, monthDate);

    const grouped = mappedEventList.reduce(
      (acc, event) => {
        if (!acc[event.date]) acc[event.date] = [];
        acc[event.date].push(event);
        return acc;
      },
      {} as Record<string, CalendarEvent[]>
    );

    return { eventList: mappedEventList, eventsByDate: grouped, usedDatelessFallback };
  }, [events, monthDate]);

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    return eachDayOfInterval({
      start: calendarStart,
      end: calendarEnd,
    });
  }, [monthDate]);

  const selectedEvents = selectedDate ? eventsByDate[selectedDate] || [] : [];

  // Get sorted list of dates with events (for navigation)
  const sortedEventDates = useMemo(() => {
    return Object.keys(eventsByDate).sort();
  }, [eventsByDate]);

  // Get current position in event dates
  const currentDateIndex = selectedDate ? sortedEventDates.indexOf(selectedDate) : -1;
  const hasPrevEvent = currentDateIndex > 0;
  const hasNextEvent = currentDateIndex < sortedEventDates.length - 1;

  // Navigation handlers
  // These handlers change the selected date AND mark dates as visited (greyed out)
  const handlePrevDate = () => {
    if (hasPrevEvent) {
      const prevDate = sortedEventDates[currentDateIndex - 1];
      setSelectedDate(prevDate);
      markDateAsVisited(prevDate);
    }
  };

  const handleNextDate = () => {
    if (hasNextEvent) {
      const nextDate = sortedEventDates[currentDateIndex + 1];
      setSelectedDate(nextDate);
      markDateAsVisited(nextDate);
    }
  };

  // Get unique platforms present in the calendar
  const availablePlatforms = useMemo(() => {
    const allPlatforms = eventList.flatMap(event => normalizePlatforms(event.platform));
    return [...new Set(allPlatforms)];
  }, [eventList]);

  const handleExploreDeeper = () => {
    if (selectedEvents.length > 0 && chatContext) {
      const allTitles = selectedEvents.map(e => e.title).join(', ');
      const message = t(
        selectedEvents.length > 1 ? 'chat.explore_deeper_multiple' : 'chat.explore_deeper_single',
        { titles: allTitles }
      );
      // Close the modal immediately
      setSelectedDate(null);
      // Send the message in the background
      chatContext.sendMessage(message);
    }
  };

  return (
    <>
      <PreviewCardShell
        className="mb-3 w-full"
        bodyClassName="p-4"
        badgeClassName="bg-teal-600"
        badge={<CalendarIcon className="h-4 w-4 text-white" />}
        label={previewLabel}
      >
      {/* Month header */}
      <div className="text-center mb-4">
        <h3 className="text-xl font-bold text-zinc-50">
          {usedDatelessFallback ? 'Content Calendar' : format(monthDate, 'MMMM yyyy')}
        </h3>
        <p className="text-xs text-zinc-400 mt-1">
          {eventList.length} posts scheduled
        </p>

        {/* Enhanced CTA - OPTION 1: Shimmer badge with pointer */}
        {!hasInteracted && (
          <div className="mt-3">
            <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full cta-shimmer text-white font-bold text-sm shadow-lg cta-bounce">
              <MousePointerClick className="h-4 w-4" />
              <span>👇 {t('common.click_days_for_details')}</span>
            </div>
          </div>
        )}
        {hasInteracted && (
          <p className="text-xs text-zinc-500 mt-2 italic">
            {t('common.click_days_for_details')}
          </p>
        )}
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-px mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="text-center text-xs font-semibold text-zinc-400 py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className={`grid grid-cols-7 gap-px bg-zinc-800 rounded-lg overflow-hidden ${!hasInteracted ? 'calendar-pulse-glow' : ''}`}>
        {calendarDays.map((day) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          return (
            <CalendarDay
              key={dateKey}
              date={day}
              events={eventsByDate[dateKey] || []}
              isCurrentMonth={isSameMonth(day, monthDate)}
              onClick={() => handleDayClick(dateKey)}
              isClicked={clickedDates.has(dateKey)}
              hideDayNumber={usedDatelessFallback}
            />
          );
        })}
      </div>

      {/* Platform legend - only show platforms present in calendar */}
      {availablePlatforms.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2 mt-4 pt-4 border-t border-zinc-800">
          {availablePlatforms.includes('instagram') && (
            <div className="flex items-center gap-1.5">
              <PlatformBadge platform="instagram" />
              <span className="text-xs text-zinc-400 hidden sm:inline">(Instagram)</span>
            </div>
          )}
          {availablePlatforms.includes('tiktok') && (
            <div className="flex items-center gap-1.5">
              <PlatformBadge platform="tiktok" />
              <span className="text-xs text-zinc-400 hidden sm:inline">(TikTok)</span>
            </div>
          )}
          {availablePlatforms.includes('youtube') && (
            <div className="flex items-center gap-1.5">
              <PlatformBadge platform="youtube" />
              <span className="text-xs text-zinc-400 hidden sm:inline">(YouTube)</span>
            </div>
          )}
        </div>
      )}
    </PreviewCardShell>

    {/* Event Details Modal */}
    {selectedDate && selectedEvents.length > 0 && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={() => setSelectedDate(null)}
      >
        <div
          className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-zinc-800">
            {/* Left arrow - only show if not first event */}
            <div className="w-10">
              {hasPrevEvent && (
                <button
                  onClick={handlePrevDate}
                  className="text-zinc-400 hover:text-zinc-200 transition-colors p-1 rounded hover:bg-zinc-800"
                  aria-label="Previous date"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Date title */}
            <h3 className="text-lg font-bold text-zinc-50 flex-1 text-center">
              {usedDatelessFallback
                ? 'Content Ideas'
                : format(new Date(selectedDate + 'T00:00:00'), 'MMMM d, yyyy')}
            </h3>

            {/* Right arrow - only show if not last event */}
            <div className="w-10 flex justify-end">
              {hasNextEvent && (
                <button
                  onClick={handleNextDate}
                  className="text-zinc-400 hover:text-zinc-200 transition-colors p-1 rounded hover:bg-zinc-800"
                  aria-label="Next date"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Event List */}
          <div className="space-y-3">
            {selectedEvents.map((event, idx) => {
              const platforms = normalizePlatforms(event.platform);
              return (
                <div
                  key={idx}
                  className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5 flex flex-wrap gap-1">
                      {platforms.map((p) => (
                        <PlatformBadge key={p} platform={p} />
                      ))}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-zinc-50 mb-1">
                        {event.title}
                      </h4>
                      {event.time && (
                        <p className="text-xs text-zinc-400">
                          {event.time}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Explore Idea Deeper Button */}
          {chatContext && (
            <button
              onClick={handleExploreDeeper}
              className="w-full mt-4 px-4 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-lg transition-all text-sm font-semibold flex items-center justify-center gap-2 shadow-lg"
            >
              <Sparkles className="h-4 w-4" />
              {t('common.explore_idea_deeper')}
            </button>
          )}

          {/* Close Button */}
          <button
            onClick={() => setSelectedDate(null)}
            className="w-full mt-4 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-50 rounded-lg transition-colors text-sm font-medium"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    )}
    </>
  );
};
